import type { ReportEntity } from './types';
import { combineTemplateParts, type TemplateParts } from './templateParts';

const ENTITY_COLORS = [
  'indigo',
  'emerald',
  'amber',
  'rose',
  'cyan',
  'violet',
  'orange',
  'teal',
];

export function getEntityColor(index: number): string {
  return ENTITY_COLORS[index % ENTITY_COLORS.length];
}

export function resolvePath(obj: unknown, path: string): unknown {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

const LEGACY_ENTITY_PLACEHOLDER_RE = /\{\{([^}]+)\}\}/g;
/** Matches `{entity.field}` without touching CSS blocks like `{ margin: 0 }`. */
const ENTITY_PLACEHOLDER_RE = /\{([a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z0-9_]+)*)\}/g;

export function formatEntityPlaceholder(path: string): string {
  return `{${path}}`;
}

function resolveEntityPlaceholder(
  trimmed: string,
  entityMap: Map<string, Record<string, unknown>>
): string | undefined {
  const dotIndex = trimmed.indexOf('.');
  if (dotIndex === -1) {
    const data = entityMap.get(trimmed.toLowerCase());
    if (data === undefined) return undefined;
    return typeof data === 'object' ? JSON.stringify(data) : String(data);
  }

  const entityName = trimmed.slice(0, dotIndex);
  const path = trimmed.slice(dotIndex + 1);
  const data = entityMap.get(entityName.toLowerCase());
  if (data === undefined) return undefined;

  const value = resolvePath(data, path);
  if (value === undefined || value === null) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function interpolateTemplate(
  html: string,
  entities: ReportEntity[]
): string {
  const entityMap = new Map(
    entities.map((e) => [e.name.toLowerCase(), e.data])
  );

  const withLegacy = html.replace(LEGACY_ENTITY_PLACEHOLDER_RE, (_, rawPath: string) => {
    const trimmed = rawPath.trim();
    const resolved = resolveEntityPlaceholder(trimmed, entityMap);
    if (resolved === undefined) return `{{${trimmed}}}`;
    return resolved;
  });

  return withLegacy.replace(ENTITY_PLACEHOLDER_RE, (match, rawPath: string) => {
    const trimmed = rawPath.trim();
    const resolved = resolveEntityPlaceholder(trimmed, entityMap);
    if (resolved === undefined) return match;
    return resolved;
  });
}

/** Convert JSX `className` to HTML `class` so CSS selectors apply in iframe preview. */
export function normalizeReportMarkup(html: string): string {
  let result = html.replace(/\sclassName(\s*=\s*)(["'])/gi, ' class$1$2');

  result = result.replace(
    /<([a-zA-Z][\w:-]*)([^>]*?)>/g,
    (full, tagName: string, attrs: string) => {
      const classMatch = attrs.match(/\sclass=(["'])([\s\S]*?)\1/i);
      const classNameMatch = attrs.match(/\sclassName=(["'])([\s\S]*?)\1/i);
      if (!classMatch || !classNameMatch) return full;

      const merged = `${classMatch[2]} ${classNameMatch[2]}`.trim();
      const quote = classMatch[1];
      let nextAttrs = attrs
        .replace(/\sclassName=(["'])([\s\S]*?)\1/i, '')
        .replace(/\sclass=(["'])([\s\S]*?)\1/i, '');
      nextAttrs = ` class=${quote}${merged}${quote}${nextAttrs}`;
      return `<${tagName}${nextAttrs}>`;
    }
  );

  return result;
}

export function buildReportPreviewHtml(
  parts: TemplateParts,
  entities: ReportEntity[]
): string {
  const combined = combineTemplateParts({
    ...parts,
    html: normalizeReportMarkup(parts.html),
  });
  return interpolateTemplate(combined, entities);
}

export function buildReportPreviewHtmlFromDocument(
  htmlTemplate: string,
  entities: ReportEntity[]
): string {
  return interpolateTemplate(normalizeReportMarkup(htmlTemplate), entities);
}

export function flattenEntityPaths(
  obj: unknown,
  prefix = ''
): string[] {
  if (obj == null || typeof obj !== 'object') {
    return prefix ? [prefix] : [];
  }

  if (Array.isArray(obj)) {
    return prefix ? [`${prefix}[*]`] : [];
  }

  const paths: string[] = [];
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    const next = prefix ? `${prefix}.${key}` : key;
    if (value != null && typeof value === 'object' && !Array.isArray(value)) {
      paths.push(...flattenEntityPaths(value, next));
    } else {
      paths.push(next);
    }
  }
  return paths;
}

export function validateEntityJson(json: string): { valid: boolean; data?: Record<string, unknown>; error?: string } {
  try {
    const parsed = JSON.parse(json);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { valid: false, error: 'JSON must be a non-null object' };
    }
    return { valid: true, data: parsed as Record<string, unknown> };
  } catch {
    return { valid: false, error: 'Invalid JSON syntax' };
  }
}

export function formatRelativeDate(iso?: string): string {
  if (!iso) return 'Never';
  const date = new Date(iso);
  const diff = Date.now() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return date.toLocaleDateString();
}

export const UNIVERSAL_REPORT_ID = '00000000-0000-0000-0000-000000000000';

export function generateReportId(): string {
  return crypto.randomUUID();
}

const ANALYTICS_HUB_VIEW_BASE =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:5174'
    : 'https://AnalyticsHub.selficai.com';

export function getAnalyticsHubLoadUrl(reportId: string): string {
  return `${ANALYTICS_HUB_VIEW_BASE}/analyticshub/load/${encodeURIComponent(reportId.trim())}`;
}

export function getAnalyticsHubEmbedUrl(reportId: string, token?: string | null): string {
  const url = getAnalyticsHubLoadUrl(reportId);
  if (!token?.trim()) return url;
  return `${url}?token=${encodeURIComponent(token.trim())}`;
}

export function getSessionAuthToken(): string {
  return localStorage.getItem('token') ?? '';
}

export function getReportAnalyticsHubLoadUrl(report: { id: string }): string {
  return getAnalyticsHubLoadUrl(report.id);
}

export function getReportAnalyticsHubViewUrl(
  report: { id: string },
  token?: string | null
): string {
  return getAnalyticsHubEmbedUrl(report.id, token ?? getSessionAuthToken());
}

export function getReportAnalyticsHubEmbedUrl(
  report: { id: string },
  token?: string | null
): string {
  return getAnalyticsHubEmbedUrl(report.id, token);
}
